import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Search, Package, FolderOpen, Layers, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchAutocomplete({ onSearch, className = '' }) {
  const { t, lang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;

  // Fetch suggestions
  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/search/suggestions?q=${encodeURIComponent(query)}&limit=10`);
        setSuggestions(res.data.suggestions || []);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } catch (err) {
        console.error('Search error:', err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        onSearch?.(query);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelect(suggestions[selectedIndex]);
        } else if (query.trim()) {
          onSearch?.(query);
          setShowDropdown(false);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelect = (item) => {
    setShowDropdown(false);
    setQuery('');
    
    if (item.type === 'category') {
      navigate(`/category/${item.id}`);
    } else if (item.type === 'subcategory') {
      navigate(`/category/${item.category_id}?sub=${item.id}`);
    } else if (item.type === 'product') {
      navigate(`/category/${item.category_id}?sub=${item.subcategory_id}&product=${item.id}`);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'category':
        return <FolderOpen className="h-4 w-4 text-[#0056A6]" />;
      case 'subcategory':
        return <Layers className="h-4 w-4 text-indigo-500" />;
      case 'product':
        return <Package className="h-4 w-4 text-emerald-500" />;
      default:
        return <Search className="h-4 w-4 text-slate-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'category':
        return t('category');
      case 'subcategory':
        return t('subcategory');
      case 'product':
        return t('products');
      default:
        return '';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute start-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={t('searchPlaceholder')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          className="ps-12 pe-4 h-14 border-0 text-base rounded-xl focus-visible:ring-[#0056A6] bg-white"
          data-testid="search-autocomplete-input"
        />
        {loading && (
          <div className="absolute end-4 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#0056A6] border-t-transparent" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50"
          data-testid="search-suggestions-dropdown"
        >
          <div className="max-h-80 overflow-y-auto">
            {suggestions.map((item, idx) => (
              <button
                key={`${item.type}-${item.id}`}
                onClick={() => handleSelect(item)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-start hover:bg-slate-50 transition-colors ${
                  selectedIndex === idx ? 'bg-blue-50' : ''
                }`}
                data-testid={`suggestion-${item.type}-${item.id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  {getIcon(item.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{getName(item)}</p>
                  <p className="text-xs text-slate-400">{getTypeLabel(item.type)}</p>
                </div>
                <ArrowRight className={`h-4 w-4 text-slate-300 flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            ))}
          </div>
          
          {/* Search all */}
          {query.trim() && (
            <button
              onClick={() => {
                onSearch?.(query);
                setShowDropdown(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 border-t border-slate-100 transition-colors"
              data-testid="search-all-btn"
            >
              <Search className="h-4 w-4 text-[#0056A6]" />
              <span className="text-sm text-[#0056A6] font-medium">
                {t('searchFor')} "{query}"
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
